import { Button, CopyField, InlineBanner } from "@sportsmet/ui";
import type { MouseEvent, ReactNode } from "react";

import {
  buildInviteMailtoHref,
  buildInviteShareBody,
  buildInviteShareSubject,
} from "../poolInvite/inviteShareHref";
import { PoolQrButton } from "../poolInvite/PoolQrButton";

/**
 * ISSUE-1316. The pool exists but its contest does not, because the league's
 * schedule for this season is not out yet (SUR-R6). "rulesSaved" means the
 * rules the fan picked are kept for later; "rulesNotSaved" means saving them
 * failed and the fan sets them up again from Settings.
 */
export type typeCreatePoolSetupPending = "rulesSaved" | "rulesNotSaved";

export const CREATE_POOL_RULES_SAVED_MESSAGE =
  "We saved your rules. This season's schedule isn't out yet, so once it is, start the season in Settings and your contest will be ready with those rules.";
export const CREATE_POOL_RULES_NOT_SAVED_MESSAGE =
  "This season's schedule isn't out yet, so your contest isn't set up. Once it is, start the season in Settings and pick your rules there.";

export type typeCreatePoolCreatedProps = {
  joinCode: string;
  joinUrl: string;
  setupPending?: typeCreatePoolSetupPending | null | undefined;
  /** Names the pool in the share message and the QR code's scan view. */
  poolName: string;
};

/**
 * The created screen. The two identifiers are always two separate fields,
 * per GLOSSARY-10 and GLOSSARY-19: Invite code and Share link, never one
 * field, never a bare "code".
 *
 * Under them, Share hands the link to the phone's share sheet (or an email
 * draft where there is none) and Show QR code puts it on screen for a friend
 * in the room (ISSUE-1339, ISSUE-1345). The dialog's footer carries Invite
 * people, which opens the pool's own invite sheet.
 */
export const CreatePoolCreated = (
  props: typeCreatePoolCreatedProps,
): ReactNode => {
  function handleShareClick(event: MouseEvent<HTMLAnchorElement>): void {
    if (
      typeof navigator === "undefined" ||
      typeof navigator.share !== "function"
    ) {
      return;
    }
    event.preventDefault();
    void navigator
      .share({
        title: buildInviteShareSubject(props.poolName),
        text: buildInviteShareBody({
          poolName: props.poolName,
          inviteUrl: props.joinUrl,
        }),
        url: props.joinUrl,
      })
      .catch(() => {
        // Dismissing the share sheet rejects. That is not an error.
      });
  }

  return (
    <div className="flex flex-col gap-4" data-testid="create-pool-created">
      {props.setupPending === "rulesSaved" ? (
        <div data-testid="create-pool-setup-pending-saved">
          <InlineBanner
            variant="neutral"
            message={CREATE_POOL_RULES_SAVED_MESSAGE}
          />
        </div>
      ) : props.setupPending === "rulesNotSaved" ? (
        <div data-testid="create-pool-setup-pending-not-saved">
          <InlineBanner
            variant="warning"
            message={CREATE_POOL_RULES_NOT_SAVED_MESSAGE}
          />
        </div>
      ) : null}
      <CopyField
        label="Invite code"
        value={props.joinCode}
        copyLabel="Copy invite code"
        hint="Secret. Anyone with it can join your pool."
        data-testid="create-pool-invite-code"
      />
      <CopyField
        label="Share link"
        value={props.joinUrl}
        copyLabel="Copy share link"
        data-testid="create-pool-share-link"
      />
      <div className="flex flex-wrap gap-2">
        <Button asChild variant="secondary" size="sm">
          <a
            href={buildInviteMailtoHref({
              poolName: props.poolName,
              inviteUrl: props.joinUrl,
            })}
            data-testid="create-pool-share"
            onClick={handleShareClick}
          >
            Share
          </a>
        </Button>
        <PoolQrButton
          inviteUrl={props.joinUrl}
          poolName={props.poolName}
          data-testid="create-pool-qr"
        />
      </div>
    </div>
  );
};
